'use client';

import { useEffect, useState } from 'react';
import { adminApi } from '@/services/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Separator } from '@/components/ui/separator';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from '@/components/ui/sonner';
import { RefreshCw, Loader2, Save } from 'lucide-react';

interface SystemSettings {
  platformName: string;
  platformDescription: string;
  supportEmail: string;
  maintenanceMode: boolean;
  registrationEnabled: boolean;
  emailVerificationRequired: boolean;
  phoneVerificationRequired: boolean;
  defaultRewardPerSms: number;
  defaultRewardPerMinute: number;
  defaultRewardPerAssignment: number;
  maxRewardsPerUserPerDay: number;
  rewardExpiryDays: number;
  minWithdrawalAmount: number;
  maxWithdrawalAmount: number;
  withdrawalFeePercent: number;
  withdrawalFeeFixed: number;
  withdrawalProcessingDays: number;
  autoApproveWithdrawals: boolean;
  referralRewardAmount: number;
  referralRewardBucket: 'bonus' | 'referral';
  maxReferralRewardsPerUser: number;
  defaultRoutingPriority: number;
  defaultRoutingWeight: number;
  healthCheckIntervalMinutes: number;
  circuitBreakerThreshold: number;
  circuitBreakerResetMinutes: number;
  defaultNumberExpiryDays: number;
  autoRenewEnabled: boolean;
  numberAssignmentBufferMinutes: number;
  quarantineDays: number;
  emailProvider: 'resend' | 'sendgrid' | 'mailgun';
  smsProvider: 'twilio' | 'plivo' | 'telnyx';
  pushProvider: 'firebase' | 'onesignal';
  jwtAccessTokenExpiryMinutes: number;
  jwtRefreshTokenExpiryDays: number;
  maxFailedLoginAttempts: number;
  lockoutDurationMinutes: number;
  passwordMinLength: number;
  requireTwoFactorForAdmins: boolean;
  whisperModel: string;
  maxAudioDurationSeconds: number;
  transcriptionLanguage: string;
  apiRateLimitPerMinute: number;
  webhookRateLimitPerMinute: number;
  csvImportMaxRows: number;
}

const defaultSettings: SystemSettings = {
  platformName: 'IPRN Online',
  platformDescription: 'Global Revenue Share Telecom Platform',
  supportEmail: 'support@iprn.online',
  maintenanceMode: false,
  registrationEnabled: true,
  emailVerificationRequired: true,
  phoneVerificationRequired: false,
  defaultRewardPerSms: 0.001,
  defaultRewardPerMinute: 0.01,
  defaultRewardPerAssignment: 0.50,
  maxRewardsPerUserPerDay: 100,
  rewardExpiryDays: 90,
  minWithdrawalAmount: 10,
  maxWithdrawalAmount: 10000,
  withdrawalFeePercent: 2.5,
  withdrawalFeeFixed: 0.50,
  withdrawalProcessingDays: 3,
  autoApproveWithdrawals: false,
  referralRewardAmount: 5.00,
  referralRewardBucket: 'bonus',
  maxReferralRewardsPerUser: 50,
  defaultRoutingPriority: 100,
  defaultRoutingWeight: 1,
  healthCheckIntervalMinutes: 5,
  circuitBreakerThreshold: 5,
  circuitBreakerResetMinutes: 15,
  defaultNumberExpiryDays: 30,
  autoRenewEnabled: true,
  numberAssignmentBufferMinutes: 5,
  quarantineDays: 7,
  emailProvider: 'resend',
  smsProvider: 'twilio',
  pushProvider: 'firebase',
  jwtAccessTokenExpiryMinutes: 15,
  jwtRefreshTokenExpiryDays: 7,
  maxFailedLoginAttempts: 5,
  lockoutDurationMinutes: 15,
  passwordMinLength: 8,
  requireTwoFactorForAdmins: true,
  whisperModel: '@cf/openai/whisper',
  maxAudioDurationSeconds: 60,
  transcriptionLanguage: 'en',
  apiRateLimitPerMinute: 100,
  webhookRateLimitPerMinute: 50,
  csvImportMaxRows: 10000,
};

const tabs = [
  { id: 'general', label: 'General' },
  { id: 'rewards', label: 'Rewards' },
  { id: 'wallet', label: 'Wallet' },
  { id: 'referrals', label: 'Referrals' },
  { id: 'providers', label: 'Providers' },
  { id: 'numbers', label: 'Numbers' },
  { id: 'notifications', label: 'Notifications' },
  { id: 'security', label: 'Security' },
  { id: 'ai', label: 'AI/Whisper' },
  { id: 'limits', label: 'Rate Limits' },
];

export function AdminSettingsPage() {
  const [settings, setSettings] = useState<SystemSettings>(defaultSettings);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState<'general' | 'rewards' | 'wallet' | 'referrals' | 'providers' | 'numbers' | 'notifications' | 'security' | 'ai' | 'limits'>('general');

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    setLoading(true);
    try {
      const response = await adminApi.getSettings();
      if (response) {
        setSettings(prev => ({ ...prev, ...response }));
      }
    } catch (err) {
      console.error('Failed to load settings:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const response = await adminApi.updateSettings(settings);
      if (response) {
        toast.success('Settings saved successfully');
      } else {
        toast.error('Failed to save settings');
      }
    } catch (err) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (confirm('Reset all settings to defaults?')) {
      setSettings(defaultSettings);
      await handleSave();
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div>
          <Skeleton className="h-8 w-48" />
          <Skeleton className="h-4 w-64 mt-2" />
        </div>
        <Card className="glass">
          <CardContent className="pt-6">
            <div className="space-y-4">
              {[1, 2, 3].map(i => <Skeleton key={i} className="h-16 w-full" />)}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-white">System Settings</h1>
          <p className="text-slate-400 mt-1">Configure platform-wide settings</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={handleReset}>
            <RefreshCw className="mr-2 h-4 w-4" />
            Reset to Defaults
          </Button>
          <Button onClick={handleSave} disabled={saving}>
            {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}Save All Settings
          </Button>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={(value: string) => setActiveTab(value as any)} className="space-y-4">
        <TabsList className="grid w-full grid-cols-5 bg-slate-800/50 overflow-x-auto">
          {tabs.map(tab => (
            <TabsTrigger key={tab.id} value={tab.id}>
              {tab.label}
            </TabsTrigger>
          ))}
        </TabsList>

        <TabsContent value="general">
          <Card className="glass">
            <CardHeader>
              <CardTitle>General Settings</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="platformName">Platform Name</Label>
                  <Input id="platformName" value={settings.platformName} onChange={e => setSettings({ ...settings, platformName: e.target.value })} />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="supportEmail">Support Email</Label>
                  <Input id="supportEmail" type="email" value={settings.supportEmail} onChange={e => setSettings({ ...settings, supportEmail: e.target.value })} />
                </div>
                <div className="space-y-2 md:col-span-2">
                  <Label htmlFor="platformDescription">Platform Description</Label>
                  <textarea id="platformDescription" className="w-full h-24 bg-slate-800/50 border border-slate-700 rounded-lg p-3 text-white" value={settings.platformDescription} onChange={e => setSettings({ ...settings, platformDescription: e.target.value })} />
                </div>
              </div>
              <Separator />
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-white">Maintenance Mode</p>
                    <p className="text-sm text-slate-400">Disable all user-facing features</p>
                  </div>
                  <Switch checked={settings.maintenanceMode} onCheckedChange={checked => setSettings({ ...settings, maintenanceMode: checked })} />
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-white">Registration Enabled</p>
                    <p className="text-sm text-slate-400">Allow new user registrations</p>
                  </div>
                  <Switch checked={settings.registrationEnabled} onCheckedChange={checked => setSettings({ ...settings, registrationEnabled: checked })} />
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-white">Email Verification Required</p>
                    <p className="text-sm text-slate-400">Users must verify email before using platform</p>
                  </div>
                  <Switch checked={settings.emailVerificationRequired} onCheckedChange={checked => setSettings({ ...settings, emailVerificationRequired: checked })} />
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-white">Phone Verification Required</p>
                    <p className="text-sm text-slate-400">Users must verify phone number</p>
                  </div>
                  <Switch checked={settings.phoneVerificationRequired} onCheckedChange={checked => setSettings({ ...settings, phoneVerificationRequired: checked })} />
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="rewards">
          <Card className="glass">
            <CardHeader>
              <CardTitle>Reward Engine Settings</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid gap-4 md:grid-cols-3">
                <div className="space-y-2">
                  <Label>Reward per SMS Received ($)</Label>
                  <Input type="number" step="0.0001" value={settings.defaultRewardPerSms} onChange={e => setSettings({ ...settings, defaultRewardPerSms: parseFloat(e.target.value) })} />
                </div>
                <div className="space-y-2">
                  <Label>Reward per Voice Minute ($)</Label>
                  <Input type="number" step="0.001" value={settings.defaultRewardPerMinute} onChange={e => setSettings({ ...settings, defaultRewardPerMinute: parseFloat(e.target.value) })} />
                </div>
                <div className="space-y-2">
                  <Label>Reward per Number Assignment ($)</Label>
                  <Input type="number" step="0.01" value={settings.defaultRewardPerAssignment} onChange={e => setSettings({ ...settings, defaultRewardPerAssignment: parseFloat(e.target.value) })} />
                </div>
              </div>
              <Separator />
              <div className="grid gap-4 md:grid-cols-3">
                <div className="space-y-2">
                  <Label>Max Rewards per User per Day</Label>
                  <Input type="number" value={settings.maxRewardsPerUserPerDay} onChange={e => setSettings({ ...settings, maxRewardsPerUserPerDay: parseInt(e.target.value) })} />
                </div>
                <div className="space-y-2">
                  <Label>Reward Expiry (Days)</Label>
                  <Input type="number" value={settings.rewardExpiryDays} onChange={e => setSettings({ ...settings, rewardExpiryDays: parseInt(e.target.value) })} />
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="wallet">
          <Card className="glass">
            <CardHeader>
              <CardTitle>Wallet & Withdrawal Settings</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid gap-4 md:grid-cols-4">
                <div className="space-y-2">
                  <Label>Min Withdrawal Amount ($)</Label>
                  <Input type="number" step="0.01" value={settings.minWithdrawalAmount} onChange={e => setSettings({ ...settings, minWithdrawalAmount: parseFloat(e.target.value) })} />
                </div>
                <div className="space-y-2">
                  <Label>Max Withdrawal Amount ($)</Label>
                  <Input type="number" step="0.01" value={settings.maxWithdrawalAmount} onChange={e => setSettings({ ...settings, maxWithdrawalAmount: parseFloat(e.target.value) })} />
                </div>
                <div className="space-y-2">
                  <Label>Withdrawal Fee (%)</Label>
                  <Input type="number" step="0.01" value={settings.withdrawalFeePercent} onChange={e => setSettings({ ...settings, withdrawalFeePercent: parseFloat(e.target.value) })} />
                </div>
                <div className="space-y-2">
                  <Label>Withdrawal Fee Fixed ($)</Label>
                  <Input type="number" step="0.01" value={settings.withdrawalFeeFixed} onChange={e => setSettings({ ...settings, withdrawalFeeFixed: parseFloat(e.target.value) })} />
                </div>
              </div>
              <Separator />
              <div className="grid gap-4 md:grid-cols-3">
                <div className="space-y-2">
                  <Label>Processing Time (Days)</Label>
                  <Input type="number" value={settings.withdrawalProcessingDays} onChange={e => setSettings({ ...settings, withdrawalProcessingDays: parseInt(e.target.value) })} />
                </div>
                <div className="space-y-2 flex items-end">
                  <Label className="w-full">Auto-Approve Withdrawals</Label>
                  <Switch checked={settings.autoApproveWithdrawals} onCheckedChange={checked => setSettings({ ...settings, autoApproveWithdrawals: checked })} />
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="referrals">
          <Card className="glass">
            <CardHeader>
              <CardTitle>Referral Program Settings</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid gap-4 md:grid-cols-3">
                <div className="space-y-2">
                  <Label>Referral Reward Amount ($)</Label>
                  <Input type="number" step="0.01" value={settings.referralRewardAmount} onChange={e => setSettings({ ...settings, referralRewardAmount: parseFloat(e.target.value) })} />
                </div>
                <div className="space-y-2">
                  <Label>Reward Bucket</Label>
                  <Select value={settings.referralRewardBucket} onValueChange={v => setSettings({ ...settings, referralRewardBucket: v as 'bonus' | 'referral' })}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select bucket" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="bonus">Bonus</SelectItem>
                      <SelectItem value="referral">Referral</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Max Referral Rewards per User</Label>
                  <Input type="number" value={settings.maxReferralRewardsPerUser} onChange={e => setSettings({ ...settings, maxReferralRewardsPerUser: parseInt(e.target.value) })} />
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="providers">
          <Card className="glass">
            <CardHeader>
              <CardTitle>Provider Settings</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid gap-4 md:grid-cols-3">
                <div className="space-y-2">
                  <Label>Default Routing Priority</Label>
                  <Input type="number" value={settings.defaultRoutingPriority} onChange={e => setSettings({ ...settings, defaultRoutingPriority: parseInt(e.target.value) })} />
                </div>
                <div className="space-y-2">
                  <Label>Default Routing Weight</Label>
                  <Input type="number" value={settings.defaultRoutingWeight} onChange={e => setSettings({ ...settings, defaultRoutingWeight: parseInt(e.target.value) })} />
                </div>
                <div className="space-y-2">
                  <Label>Health Check Interval (Minutes)</Label>
                  <Input type="number" value={settings.healthCheckIntervalMinutes} onChange={e => setSettings({ ...settings, healthCheckIntervalMinutes: parseInt(e.target.value) })} />
                </div>
              </div>
              <Separator />
              <div className="grid gap-4 md:grid-cols-3">
                <div className="space-y-2">
                  <Label>Circuit Breaker Threshold</Label>
                  <Input type="number" value={settings.circuitBreakerThreshold} onChange={e => setSettings({ ...settings, circuitBreakerThreshold: parseInt(e.target.value) })} />
                </div>
                <div className="space-y-2">
                  <Label>Circuit Breaker Reset (Minutes)</Label>
                  <Input type="number" value={settings.circuitBreakerResetMinutes} onChange={e => setSettings({ ...settings, circuitBreakerResetMinutes: parseInt(e.target.value) })} />
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="numbers">
          <Card className="glass">
            <CardHeader>
              <CardTitle>Number Management Settings</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid gap-4 md:grid-cols-4">
                <div className="space-y-2">
                  <Label>Default Expiry (Days)</Label>
                  <Input type="number" value={settings.defaultNumberExpiryDays} onChange={e => setSettings({ ...settings, defaultNumberExpiryDays: parseInt(e.target.value) })} />
                </div>
                <div className="space-y-2">
                  <Label>Assignment Buffer (Minutes)</Label>
                  <Input type="number" value={settings.numberAssignmentBufferMinutes} onChange={e => setSettings({ ...settings, numberAssignmentBufferMinutes: parseInt(e.target.value) })} />
                </div>
                <div className="space-y-2">
                  <Label>Quarantine Period (Days)</Label>
                  <Input type="number" value={settings.quarantineDays} onChange={e => setSettings({ ...settings, quarantineDays: parseInt(e.target.value) })} />
                </div>
                <div className="space-y-2 flex items-end">
                  <Label className="w-full">Auto-Renew Enabled</Label>
                  <Switch checked={settings.autoRenewEnabled} onCheckedChange={checked => setSettings({ ...settings, autoRenewEnabled: checked })} />
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="notifications">
          <Card className="glass">
            <CardHeader>
              <CardTitle>Notification Providers</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid gap-4 md:grid-cols-3">
                <div className="space-y-2">
                  <Label>Email Provider</Label>
                  <Select value={settings.emailProvider} onValueChange={v => setSettings({ ...settings, emailProvider: v as any })}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select provider" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="resend">Resend</SelectItem>
                      <SelectItem value="sendgrid">SendGrid</SelectItem>
                      <SelectItem value="mailgun">Mailgun</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>SMS Provider</Label>
                  <Select value={settings.smsProvider} onValueChange={v => setSettings({ ...settings, smsProvider: v as any })}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select provider" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="twilio">Twilio</SelectItem>
                      <SelectItem value="plivo">Plivo</SelectItem>
                      <SelectItem value="telnyx">Telnyx</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Push Provider</Label>
                  <Select value={settings.pushProvider} onValueChange={v => setSettings({ ...settings, pushProvider: v as any })}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select provider" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="firebase">Firebase</SelectItem>
                      <SelectItem value="onesignal">OneSignal</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="security">
          <Card className="glass">
            <CardHeader>
              <CardTitle>Security Settings</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid gap-4 md:grid-cols-3">
                <div className="space-y-2">
                  <Label>JWT Access Token Expiry (Minutes)</Label>
                  <Input type="number" value={settings.jwtAccessTokenExpiryMinutes} onChange={e => setSettings({ ...settings, jwtAccessTokenExpiryMinutes: parseInt(e.target.value) })} />
                </div>
                <div className="space-y-2">
                  <Label>JWT Refresh Token Expiry (Days)</Label>
                  <Input type="number" value={settings.jwtRefreshTokenExpiryDays} onChange={e => setSettings({ ...settings, jwtRefreshTokenExpiryDays: parseInt(e.target.value) })} />
                </div>
                <div className="space-y-2">
                  <Label>Max Failed Login Attempts</Label>
                  <Input type="number" value={settings.maxFailedLoginAttempts} onChange={e => setSettings({ ...settings, maxFailedLoginAttempts: parseInt(e.target.value) })} />
                </div>
              </div>
              <Separator />
              <div className="grid gap-4 md:grid-cols-3">
                <div className="space-y-2">
                  <Label>Lockout Duration (Minutes)</Label>
                  <Input type="number" value={settings.lockoutDurationMinutes} onChange={e => setSettings({ ...settings, lockoutDurationMinutes: parseInt(e.target.value) })} />
                </div>
                <div className="space-y-2">
                  <Label>Password Min Length</Label>
                  <Input type="number" value={settings.passwordMinLength} onChange={e => setSettings({ ...settings, passwordMinLength: parseInt(e.target.value) })} />
                </div>
                <div className="space-y-2 flex items-end">
                  <Label className="w-full">Require 2FA for Admins</Label>
                  <Switch checked={settings.requireTwoFactorForAdmins} onCheckedChange={checked => setSettings({ ...settings, requireTwoFactorForAdmins: checked })} />
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="ai">
          <Card className="glass">
            <CardHeader>
              <CardTitle>AI / Whisper Settings</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid gap-4 md:grid-cols-3">
                <div className="space-y-2">
                  <Label>Whisper Model</Label>
                  <Input value={settings.whisperModel} onChange={e => setSettings({ ...settings, whisperModel: e.target.value })} placeholder="@cf/openai/whisper" />
                </div>
                <div className="space-y-2">
                  <Label>Max Audio Duration (Seconds)</Label>
                  <Input type="number" value={settings.maxAudioDurationSeconds} onChange={e => setSettings({ ...settings, maxAudioDurationSeconds: parseInt(e.target.value) })} />
                </div>
                <div className="space-y-2">
                  <Label>Transcription Language</Label>
                  <Input value={settings.transcriptionLanguage} onChange={e => setSettings({ ...settings, transcriptionLanguage: e.target.value })} placeholder="en" />
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="limits">
          <Card className="glass">
            <CardHeader>
              <CardTitle>Rate Limits & Import Limits</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid gap-4 md:grid-cols-3">
                <div className="space-y-2">
                  <Label>API Rate Limit (per minute)</Label>
                  <Input type="number" value={settings.apiRateLimitPerMinute} onChange={e => setSettings({ ...settings, apiRateLimitPerMinute: parseInt(e.target.value) })} />
                </div>
                <div className="space-y-2">
                  <Label>Webhook Rate Limit (per minute)</Label>
                  <Input type="number" value={settings.webhookRateLimitPerMinute} onChange={e => setSettings({ ...settings, webhookRateLimitPerMinute: parseInt(e.target.value) })} />
                </div>
                <div className="space-y-2">
                  <Label>CSV Import Max Rows</Label>
                  <Input type="number" value={settings.csvImportMaxRows} onChange={e => setSettings({ ...settings, csvImportMaxRows: parseInt(e.target.value) })} />
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
